import { Dialog, Stack } from '@fluentui/react';
import { Button, buttonStylesPrimary } from '@h2oai/ui-kit';
import { useEffect, useState } from 'react';

import { type FileValidationResult, sanitizeS3ObjectKey, validateS3ObjectKey } from '../../../../drive/s3Validation';
import { joinedButtonsStyles } from '../../../../utils/styles';
import { FileValidationItem } from './FileValidationItem';

interface FileNameValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (correctedFiles: { originalName: string; correctedName: string }[]) => void;
  invalidFiles: FileValidationResult[];
  existingKeys?: string[];
}

interface CorrectedFile {
  originalName: string;
  correctedName: string;
  validationResult: ReturnType<typeof validateS3ObjectKey>;
  overrideExisting: boolean;
  skip: boolean;
}

export const FileNameValidationModal = (props: FileNameValidationModalProps) => {
  const { isOpen, onClose, onConfirm, invalidFiles, existingKeys } = props;
  const [correctedFiles, setCorrectedFiles] = useState<CorrectedFile[]>([]);

  useEffect(() => {
    setCorrectedFiles(
      invalidFiles.map((file) => ({
        originalName: file.originalName,
        correctedName: file.originalName,
        validationResult: file.validationResult,
        overrideExisting: false,
        skip: false, // No default selection
      }))
    );
  }, [invalidFiles]);

  const handleFileNameChange = (index: number, newName: string) => {
    const updated = [...correctedFiles];
    const newValidation = validateS3ObjectKey(newName, existingKeys);
    updated[index] = {
      ...updated[index],
      correctedName: newName,
      validationResult: newValidation,
      overrideExisting: false, // Reset when name changes
      skip: false, // Reset when name changes
    };
    setCorrectedFiles(updated);
  };

  const handleOverrideClick = (index: number) => {
    const updated = [...correctedFiles];
    const currentFile = updated[index];
    updated[index] = {
      ...currentFile,
      overrideExisting: !currentFile.overrideExisting,
      skip: false,
    };
    setCorrectedFiles(updated);
  };

  const handleSkipClick = (index: number) => {
    const updated = [...correctedFiles];
    const currentFile = updated[index];
    updated[index] = {
      ...currentFile,
      overrideExisting: false,
      skip: !currentFile.skip,
    };
    setCorrectedFiles(updated);
  };

  const handleConfirm = () => {
    const allValid = correctedFiles.every(
      (file) =>
        file.skip ||
        file.validationResult.isValid ||
        (file.validationResult.errors.some((error) => error.includes('already exists')) && file.overrideExisting)
    );
    if (allValid) {
      onConfirm(
        correctedFiles
          .filter((file) => !file.skip)
          .map((file) => ({
            originalName: file.originalName,
            correctedName: file.correctedName,
          }))
      );
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleFixAll = () => {
    const updated = correctedFiles.map((file) => {
      const sanitizedName = sanitizeS3ObjectKey(file.originalName);
      const newValidation = validateS3ObjectKey(sanitizedName, existingKeys);
      return {
        ...file,
        correctedName: sanitizedName,
        validationResult: newValidation,
        overrideExisting: false,
        skip: false, // Reset choice after fixing
      };
    });
    setCorrectedFiles(updated);
  };

  const handleOverrideAll = () => {
    const updated = correctedFiles.map((file) => {
      const isDuplicate = file.validationResult.errors.some((error) => error.includes('already exists'));
      if (!isDuplicate) return file;

      return {
        ...file,
        overrideExisting: true,
        skip: false,
      };
    });
    setCorrectedFiles(updated);
  };

  const handleSkipAll = () => {
    const updated = correctedFiles.map((file) => {
      const isDuplicate = file.validationResult.errors.some((error) => error.includes('already exists'));
      if (!isDuplicate) return file;

      return {
        ...file,
        overrideExisting: false,
        skip: true,
      };
    });
    setCorrectedFiles(updated);
  };

  const duplicateFiles = correctedFiles.filter((file) =>
    file.validationResult.errors.some((error) => error.includes('already exists'))
  );

  const allFilesValid = correctedFiles.every((file) => {
    const isDuplicate = file.validationResult.errors.some((error) => error.includes('already exists'));
    if (isDuplicate) {
      return file.skip || file.overrideExisting; // Must have a choice
    }
    return file.validationResult.isValid;
  });

  const allDuplicatesOverridden = duplicateFiles.length > 0 && duplicateFiles.every((file) => file.overrideExisting);
  const allDuplicatesSkipped = duplicateFiles.length > 0 && duplicateFiles.every((file) => file.skip);

  const fixableFiles = correctedFiles.filter(
    (file) =>
      !file.validationResult.isValid && !file.validationResult.errors.some((error) => error.includes('already exists'))
  );
  const hasFixableFiles = fixableFiles.length > 0;
  const hasDuplicateFiles = duplicateFiles.length > 0;

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={handleCancel}
      dialogContentProps={{ title: 'Fixing validation issues' }}
      minWidth={600}
      modalProps={{ isBlocking: true }}
      data-test="file-validation-modal"
    >
      <Stack tokens={{ childrenGap: 16 }}>
        <Stack>
          <p style={{ margin: 0 }}>
            <strong>Naming Rules:</strong>
          </p>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            <li>Cannot have leading or trailing spaces</li>
            <li>Cannot contain: &amp; $ @ = ; : + , ? \ {'{ ^ } % `'} [ ] &quot; &lt; &gt; ~ # |</li>
            <li>Cannot consist only of dots</li>
            <li>Maximum length: 1024 bytes</li>
          </ul>
        </Stack>

        <Stack tokens={{ childrenGap: 12 }} styles={{ root: { maxHeight: '50vh', overflow: 'auto' } }}>
          {correctedFiles.map((file, index) => (
            <FileValidationItem
              key={file.originalName}
              originalName={file.originalName}
              correctedName={file.correctedName}
              validationResult={file.validationResult}
              overrideExisting={file.overrideExisting}
              skip={file.skip}
              onNameChange={(newName) => handleFileNameChange(index, newName)}
              onOverrideClick={() => handleOverrideClick(index)}
              onSkipClick={() => handleSkipClick(index)}
              index={index}
            />
          ))}
        </Stack>
      </Stack>

      <Stack
        horizontal
        tokens={{ childrenGap: 8 }}
        horizontalAlign="space-between"
        styles={{ root: { width: '100%', paddingTop: 16 } }}
      >
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          {hasFixableFiles && <Button text="Fix all" onClick={handleFixAll} data-test="fix-all-button" />}
          {hasDuplicateFiles && (
            <Stack horizontal styles={joinedButtonsStyles}>
              <Button
                text="Override all"
                onClick={handleOverrideAll}
                styles={allDuplicatesOverridden ? buttonStylesPrimary : undefined}
                data-test="override-all-button"
              />
              <Button
                text="Skip all"
                onClick={handleSkipAll}
                styles={allDuplicatesSkipped ? buttonStylesPrimary : undefined}
                data-test="skip-all-button"
              />
            </Stack>
          )}
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <Button text="Cancel" onClick={handleCancel} data-test="cancel-button" />
          <Button
            styles={buttonStylesPrimary}
            text="Upload Files"
            onClick={handleConfirm}
            disabled={!allFilesValid}
            data-test="upload-files-button"
          />
        </Stack>
      </Stack>
    </Dialog>
  );
};
